import {
  Box,
  Button,
  TableBody as MuiTableBody,
  TableRow as MuiTableRow,
  TableCell as MuiTableCell,
  Table as MuiTable,
  TableHead as MuiTableHead,
  Switch,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  Avatar,
  TextField,
  Radio,
  OutlinedInput,
  SelectChangeEvent,
} from '@mui/material';
import styles from './styles';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useToastContext } from '../../../../../../components/Toast/toastContext';
import { Settings, Info, DragIndicator } from '@mui/icons-material';
import { Dialog } from '../../../../../../components/Dialog';
import { ColorPicker } from '../../../../../../components/ColorPicker';
import apis from '../../../../../../apis';
import { CellConfigDialog } from '../../../../../../components/Dialogs/CellConfig';
import theme from '../../../../../../theme';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { AutomaticCellConfigDialog } from '../../../../../../components/Dialogs';
import { ColumnDeleteIcon, RowDeleteIcon } from '../../../../../../assets/icons';
import { RowSettingIcon } from '../../../../../../assets/icons';

type Props = {
  id?: number;
  isin: string;
  content?: any;
  originalContent?: any;
  preview: boolean;
  onChange: (value: any) => void;
};

export const Table = ({ isin, content, originalContent, onChange }: Props) => {
  console.log(content);
  const { activeToast } = useToastContext();
  const [columnSettingIndex, setColumnSettingIndex] = useState<number | null>(null);
  const [selectedPos, setSelectedPos] = useState<number[] | null>();
  const [selectedCellPos, setSelectedCellPos] = useState<[number, number] | null>(null);
  const [openTitleConfigDlg, setOpenTitleConfigDlg] = useState(false);
  const [selectedRowPos, setSelectedRowPos] = useState<number | null>(null);
  const [stagedRowSetting, setStagedRowSetting] = useState<any>({});
  const [confirmRowIndex, setConfirmRowIndex] = useState<number | null>(null);
  const [confirmColumnIndexForApply, setConfirmColumnIndexForApply] = useState<number | null>(null);
  const [confirmEnabled, setConfirmEnabled] = useState<boolean>(false);
  const [confirmForm, setConfirmForm] = useState<{ title: string; text: string; cancelText: string; confirmText: string; cancelAlertTitle: string; cancelAlertText: string }>({ title: '', text: '', cancelText: '', confirmText: '', cancelAlertTitle: '', cancelAlertText: '' });
  const [showCancelAlert, setShowCancelAlert] = useState<boolean>(false);

  const handleAddColumn = () => {
    const header = content.header;
    const body = content.body;
    onChange({
      ...content,
      header: [...header, ''],
      body: body.map((row) => [...row, '']),
      columns: [
        ...content.columns,
        { type: 'text', ...(content.type === 'Automatic' && { showValueConfiguration: true }) },
      ],
    });
  };

  const handleAddRow = () => {
    const body = content.body;
    const header = content.header;
    onChange({ ...content, body: body.concat([header.map(() => '')]) });
  };

  const handleChangeCell = (rowIndex, columnIndex, e, isObject) => {
    const body = content.body;
    onChange({
      ...content,
      body: body.map((row, rIndex) =>
        rIndex === rowIndex
          ? row.map((column, cIndex) =>
            cIndex === columnIndex ? (!isObject ? e.target.value : { ...column, text: e.target.value }) : column
          )
          : row
      ),
    });
  };

  const handleChangeLink = (rowIndex, columnIndex, e) => {
    const body = content.body;
    onChange({
      ...content,
      body: body.map((row, rIndex) =>
        rIndex === rowIndex
          ? row.map((column, cIndex) =>
            cIndex === columnIndex
              ? {
                ...column,
                linkUrl: column?.linkUrl || '',
                linkText: column?.linkText || '',
                [e.target.name]: e.target.value,
              }
              : column
          )
          : row
      ),
    });
  };

  const handleChangeHeader = (columnIndex, e) => {
    onChange({
      ...content,
      header: content.header.map((cell, index) => (index === columnIndex ? e.target.value : cell)),
    });
  };

  const handleToggleHeader = () => {
    onChange({
      ...content,
      showHeader: !content.showHeader,
    });
  };

  const handleChangeCountOfRole = (value: string | number) => {
    onChange({
      ...content,
      countOfRow: value,
    });
  };

  const handleRemoveColumn = (columnIndex) => {
    onChange({
      ...content,
      header: content.header.filter((_, index) => index !== columnIndex),
      body: content.body.map((row) => row.filter((_, index) => index !== columnIndex)),
      columns: content.columns.filter((_, index) => index !== columnIndex),
    });
  };

  const handleRemoveRow = (rowIndex) => {
    onChange({
      ...content,
      body: content.body.filter((_, index) => index !== rowIndex),
    });
  };

  const handleOpenColumSetting = (column: number) => {
    setColumnSettingIndex(column + 1);
  };

  const handleCloseColumnSetting = () => {
    setColumnSettingIndex(null);
  };

  const handleChangeColumnSetting = (e) => {
    onChange({
      ...content,
      columns: content.columns.map((col, index) =>
        columnSettingIndex && index === columnSettingIndex - 1 ? { ...col, type: e.target.value } : col
      ),
    });
  };

  const fileSelector = useRef<any>();

  const handleOpenBrowser = (row, col) => {
    setSelectedPos([row, col]);
    fileSelector?.current?.click();
  };

  const handleUpdateLogo = async (e: any) => {
    if (!selectedPos) {
      return;
    }

    const [rowIndex, columnIndex] = selectedPos;
    if (e.target.files[0]) {
      apis.cdn.uploadImage(e.target.files[0]).then((res) => {
        if (res.status) {
          onChange({
            ...content,
            body: content.body.map((row, rIndex) =>
              rIndex === rowIndex ? row.map((column, cIndex) => (cIndex === columnIndex ? res.fileName : column)) : row
            ),
          });
        }
      });
    }
  };

  // Row confirm dialog helpers (store under body per row)
  const handleOpenRowConfirmDialog = (row: number, col: number) => {
    setConfirmRowIndex(row + 1);
    setConfirmColumnIndexForApply(col);
    const firstCell = content.body?.[row]?.[col];
    const existing = typeof firstCell === 'object' && firstCell?.rowConfirm ? firstCell.rowConfirm : {};
    const enabled = !!existing?.enabled;
    setConfirmEnabled(enabled);
    setShowCancelAlert(!!existing?.dialog?.showCancelAlert);
    setConfirmForm({
      title: existing?.dialog?.title || '',
      text: existing?.dialog?.text || '',
      cancelText: existing?.dialog?.cancelText || '',
      confirmText: existing?.dialog?.confirmText || '',
      cancelAlertTitle: existing?.dialog?.cancelAlertTitle || '',
      cancelAlertText: existing?.dialog?.cancelAlertText || '',
    });
  };

  const handleCloseRowConfirmDialog = () => {
    setConfirmRowIndex(null);
  };

  const handleChangeConfirmField = (field: 'title' | 'text' | 'cancelText' | 'confirmText' | 'cancelAlertTitle' | 'cancelAlertText') => (e: ChangeEvent<HTMLInputElement>) => {
    setConfirmForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSaveRowConfirmDialog = () => {
    if (!confirmRowIndex) return;
    const targetRow = confirmRowIndex - 1;
    const targetCol = confirmColumnIndexForApply ?? 0;
    const updatedBody = (content.body || []).map((row, idx) => {
      if (idx !== targetRow) return row;
      const cell = row?.[targetCol];
      const ensured = typeof cell === 'object' ? { ...cell } : { text: cell };
      ensured.rowConfirm = { enabled: confirmEnabled, dialog: { ...confirmForm, showCancelAlert } };
      const newRow = [...row];
      newRow[targetCol] = ensured;
      return newRow;
    });
    onChange({ ...content, body: updatedBody });
    setConfirmRowIndex(null);
    setConfirmColumnIndexForApply(null);
  };

  const handleApplyRowConfirmToColumn = () => {
    if (confirmColumnIndexForApply === null) return;
    const targetCol = confirmColumnIndexForApply;
    const updatedBody = (content.body || []).map((row) => {
      const cell = row?.[targetCol];
      const ensured = typeof cell === 'object' ? { ...cell } : { text: cell };
      ensured.rowConfirm = { enabled: confirmEnabled, dialog: { ...confirmForm, showCancelAlert } };
      const newRow = [...row];
      newRow[targetCol] = ensured;
      return newRow;
    });
    onChange({ ...content, body: updatedBody });
    activeToast({ type: 'success', text: 'Applied to all rows' });
  };

  // Initialize staged row settings when opening the Row Style Settings dialog
  useEffect(() => {
    if (selectedRowPos !== null) {
      const list = Array.isArray(content.rowSetting) ? content.rowSetting : [];
      const byRowPos = list.find((s: any) => s && s.rowPos === selectedRowPos);
      const initial = { rowPos: selectedRowPos, ...(byRowPos || {}) };
      setStagedRowSetting(initial);
    }
  }, [selectedRowPos, content.rowSetting]);

  const handleRowSettingFieldChange = (field: string, value: any) => {
    setStagedRowSetting((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleCancelRowSetting = () => {
    setSelectedRowPos(null);
    setStagedRowSetting({});
  };

  const handleCommitRowSetting = () => {
    if (selectedRowPos === null) return;
    const list = Array.isArray(content.rowSetting) ? [...content.rowSetting] : [];
    const indexByRowPos = list.findIndex((s: any) => s && s.rowPos === selectedRowPos);
    if (indexByRowPos >= 0) {
      list[indexByRowPos] = { ...(list[indexByRowPos] || {}), ...stagedRowSetting, rowPos: selectedRowPos };
    } else {
      list.push({ ...stagedRowSetting, rowPos: selectedRowPos });
    }
    onChange({
      ...content,
      rowSetting: list,
    });
    setSelectedRowPos(null);
    setStagedRowSetting({});
  };

  const handleChangeAddToolTip = (e) => {
    onChange({
      ...content,
      columns: content.columns.map((col, index) =>
        columnSettingIndex && index === columnSettingIndex - 1
          ? { ...col, showColumnTooltip: e.target.checked, columnTooltip: '' }
          : col
      ),
    });
  };

  const handleChangeValueConfigOption = (e) => {
    onChange({
      ...content,
      columns: content.columns.map((col, index) =>
        columnSettingIndex && index === columnSettingIndex - 1
          ? { ...col, showValueConfiguration: e.target.checked }
          : col
      ),
    });
  };

  const handleChangeColumnTooltipText = (e) => {
    onChange({
      ...content,
      columns: content.columns.map((col, index) =>
        columnSettingIndex && index === columnSettingIndex - 1 ? { ...col, columnTooltip: e.target.value } : col
      ),
    });
  };

  const handleChangeThemeIndex = (index: number) => {
    onChange({
      ...content,
      themeIndex: index,
    });
  };

  const handleChangeType = (e: SelectChangeEvent) => {
    const type = e.target.value;

    if (type === 'Preis') {
      onChange({
        ...content,
        columns: [{ type: 'text' }, { type: 'text' }],
        showHeader: true,
        header: ['Anteilsklasse', 'R'],
        body: [
          ['Kurs per', { text: '$datum$', type: 'date' }],
          ['Währung', 'EUR'],
          ['Ausgabe- / Rücknahmepreis', { text: '$ausgabepreis$/$ruecknahmepreis$', type: 'number' }],
          ['Fondsvolumen', { text: '$fondsvermoegen$ Milo. EUR', type: 'number' }],
          ['Letzte Ausschüttung', '0,15 EUR'],
        ],
        type,
      });
    } else if (type === 'Manual') {
      onChange({
        columns: originalContent.content.columns,
        showHeader: true,
        header: originalContent.content.header,
        body: originalContent.content.body,
        type,
      });
    } else if (type === 'Automatic') {
      onChange({
        ...content,
        columns: [{ type: 'text', showValueConfiguration: true }],
        showHeader: true,
        header: [''],
        countOfRow: 'All',
        body: [['']],
        type,
      });
    } else {
      onChange({
        ...content,
        columns: [{ type: 'text', showValueConfiguration: true }],
        showHeader: true,
        header: [''],
        body: [['']],
        type,
      });
    }
  };

  const handleChangeIsin = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...content,
      isin: e.target.value,
    });
  };

  const handleToggleLinkWithPdfField = () => {
    onChange({
      ...content,
      isLinkedWithPdfField: !content.isLinkedWithPdfField,
    });
  };

  const handleChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...content, title: e.target.value });
  };

  const handleChangeTitleConfig = (config: any) => {
    onChange({ ...content, title: config });
    setOpenTitleConfigDlg(false);
  };

  const handleToggleDynamicTitle = () => {
    onChange({
      ...content,
      title: content.title?.isDynamic
        ? ''
        : {
          isDynamic: true,
          fields: [],
        },
    });
  };

  const handleOpenCellConfigDialog = (row: number, col: number) => {
    setSelectedCellPos([row, col]);
  };

  const handleCloseCellConfigDialog = () => {
    setSelectedCellPos(null);
  };

  const handleOpenRowSetting = (row: number) => {
    setSelectedRowPos(row);
  };

  const handleSetCellConfig = (data: any) => {
    if (selectedCellPos) {
      if (content.type !== 'Automatic') {
        const body = content.body;
        onChange({
          ...content,
          body: body.map((row, rIndex) =>
            rIndex === selectedCellPos[0]
              ? row.map((column, cIndex) => (cIndex === selectedCellPos[1] ? data : column))
              : row
          ),
        });
      } else {
        const body = content.body;
        const emptyRow: any[] = [];
        onChange({
          ...content,
          body:
            body.length > 0
              ? body.map((row, rIndex) =>
                rIndex === selectedCellPos[0]
                  ? row.map((column, cIndex) => (cIndex === selectedCellPos[1] ? data : column))
                  : row
              )
              : [[(emptyRow[selectedCellPos[1]] = data)]],
        });
      }
    }
    setSelectedCellPos(null);
  };

  const handleSetPdfTableKey = (e: SelectChangeEvent) => {
    onChange({
      ...content,
      pdfTableKey: e.target.value,
    });
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    const arr = [...content.body];

    const removedItem = arr.splice(result.source.index, 1)[0];
    arr.splice(result.destination.index, 0, removedItem);
    onChange({ ...content, body: arr });
  };

  const handleOpenTitleConfig = () => {
    setOpenTitleConfigDlg(!openTitleConfigDlg);
  };

  return (
    <Box>
      <Typography variant="h5">Table</Typography>

      <Box display="flex" alignItems="center" gap={16} mb={16}>
        <Typography variant="subtitle1">Title:</Typography>
        {content.title?.isDynamic ? (
          <>
            <Box sx={styles.dataConfigInputWrapper}>
              {content.title?.fields?.map((field, index) => (
                <Box key={`${index}th`} sx={(theme) => styles.configField(theme, { type: field.type })}>
                  <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap' }}>
                    {field.type === 'text' ? field.text : `${field.table || '(table)'}.${field.field || '(field)'}`}
                  </Typography>
                </Box>
              ))}
            </Box>
            <CellConfigDialog
              isin={content.isin}
              config={content.title}
              open={openTitleConfigDlg}
              onClose={() => setOpenTitleConfigDlg(false)}
              onConfirm={handleChangeTitleConfig}
            />
            <IconButton sx={{ mr: 4, width: 32, height: 32 }} onClick={handleOpenTitleConfig}>
              <Settings />
            </IconButton>
          </>
        ) : (
          <OutlinedInput
            sx={{ width: '300px' }}
            name="title"
            type="string"
            value={content.title || ''}
            onChange={handleChangeTitle}
          />
        )}
        <FormControlLabel
          label="Is dynamic?"
          sx={styles.switch}
          control={<Switch checked={content.title?.isDynamic} color="secondary" onChange={handleToggleDynamicTitle} />}
        />
      </Box>

      <Box display="flex" sx={{ my: 20 }}>
        <Box>
          <Select sx={{ width: '300px' }} value={content?.type || 'Manual'} onChange={handleChangeType}>
            <MenuItem value="Manual">Manual</MenuItem>
            <MenuItem value="Automatic">Automatic</MenuItem>
            <MenuItem value="Axxion">Axxion</MenuItem>
            <MenuItem value="Preis">Preis</MenuItem>
          </Select>
        </Box>

        {content?.type === 'Axxion' && (
          <Box sx={{ ml: '30px' }}>
            <OutlinedInput value={content?.isin || ''} onChange={handleChangeIsin} placeholder="ISIN" />
          </Box>
        )}
      </Box>

      <Box display="flex" sx={{ mb: '8px' }}>
        <FormControlLabel
          label="Is linked with PDF field"
          control={
            <Switch checked={content.isLinkedWithPdfField} color="secondary" onChange={handleToggleLinkWithPdfField} />
          }
        />

        {content.isLinkedWithPdfField && (
          <Select sx={{ width: '300px' }} value={content?.pdfTableKey} onChange={handleSetPdfTableKey}>
            <MenuItem value="risikoindikatoren">RISIKOINDIKATOREN</MenuItem>
            <MenuItem value="wertentwicklung_nach_perioden">WERTENTWICKLUNG NACH PERIODEN</MenuItem>
          </Select>
        )}
      </Box>

      <Box display="flex" alignItems="center">
        <Button sx={{ marginRight: 10 }} onClick={handleAddColumn}>
          Add Column
        </Button>
        {content.type !== 'Automatic' && (
          <Button sx={{ marginRight: 10 }} onClick={handleAddRow}>
            Add Row
          </Button>
        )}
        <FormControlLabel
          label="Show Header"
          sx={styles.switch}
          control={<Switch checked={content.showHeader} color="secondary" onChange={handleToggleHeader} />}
        />
        {content.type === 'Automatic' && (
          <>
            <Typography variant="subtitle2" sx={{ whiteSpace: 'nowrap', mr: 8 }}>
              Count of Rows
            </Typography>
            <Box display="flex" className="">
              {new Array(5).fill(0).map((_, i) => (
                <Box
                  key={i}
                  sx={(theme) =>
                    styles.rowCountItem(theme, {
                      isFirst: i === 0,
                      isLast: false,
                      active: (content.countOfRow || 'All') === (i + 1) * 5,
                    })
                  }
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={40}
                  height={40}
                  onClick={() => handleChangeCountOfRole((i + 1) * 5)}
                >
                  {(i + 1) * 5}
                </Box>
              ))}
              <Box
                sx={(theme) =>
                  styles.rowCountItem(theme, {
                    isFirst: false,
                    active: (content.countOfRow || 'All') === 'All',
                    isLast: true,
                  })
                }
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={40}
                height={40}
                onClick={() => handleChangeCountOfRole('All')}
              >
                All
              </Box>
            </Box>
          </>
        )}
      </Box>
      <Box display="flex" gap={16} my={8}>
        {[0, 1, 2].map((color, index) => (
          <Box display="flex" flexDirection="column" alignItems="center">
            <Radio
              color="secondary"
              checked={index === content.themeIndex}
              size="medium"
              onChange={() => handleChangeThemeIndex(index)}
            />
            <Typography variant="subtitle2" sx={{ color }}>
              Table Theme {index}
            </Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: 0, flexGrow: 1, overflow: 'auto', border: `1px solid ${theme.palette.secondary.main}` }}>
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId={`${content.title}-table`}>
              {(provided) => (
                <MuiTable {...provided.droppableProps} ref={provided.innerRef}>
                  <MuiTableHead sx={styles.tableHeader}>
                    <MuiTableRow sx={styles.tableRow}>
                      {content?.header?.map((cell, columnIndex) => (
                        <MuiTableCell key={columnIndex} sx={styles.tableCell}>
                          <Box display="flex" alignItems="center">
                            <input
                              disabled={!content.showHeader}
                              value={cell}
                              onChange={(e) => handleChangeHeader(columnIndex, e)}
                            />
                            {content.columns && content.columns[columnIndex]?.showColumnTooltip && (
                              <Tooltip title={content.columns[columnIndex].columnTooltip}>
                                <Box display="flex" alignItems="center" mr={8}>
                                  <Info />
                                </Box>
                              </Tooltip>
                            )}
                            <Tooltip title="Column Setting">
                              <IconButton sx={{ mr: 4 }} onClick={() => handleOpenColumSetting(columnIndex)}>
                                <Settings />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove Column">
                              <IconButton
                                onClick={() => handleRemoveColumn(columnIndex)}
                                sx={{ svg: { flexShrink: 0, width: 22, height: 22 } }}
                              >
                                <ColumnDeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </MuiTableCell>
                      ))}
                    </MuiTableRow>
                  </MuiTableHead>
                  <MuiTableBody>
                    {content?.body?.map((row, rowIndex) => (
                      <Draggable key={row.id} draggableId={`${content.title}-row-${rowIndex}`} index={rowIndex}>
                        {(provided, snapshot) => (
                          <MuiTableRow
                            sx={styles.tableRow}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            style={{
                              background: snapshot.isDragging ? '#ecb6d680' : 'white',
                              ...(snapshot.isDragging && { display: 'flex' }),
                              ...provided.draggableProps.style,
                            }}
                          >
                            {row.map((cell, columnIndex) => (
                              <MuiTableCell
                                sx={styles.tableCell}
                                style={{ ...(snapshot.isDragging && { flex: 1, height: '100%' }) }}
                              >
                                <Box width="100%" display="flex" alignItems="center">
                                  {columnIndex === 0 && (
                                    <Box sx={styles.dragIndicator} className="drag-handle" {...provided.dragHandleProps}>
                                      <DragIndicator />
                                    </Box>
                                  )}
                                  {content.columns &&
                                    content.columns[columnIndex]?.type === 'text' &&
                                    content.columns[columnIndex].showValueConfiguration ? (
                                    <Box sx={styles.configWrapper}>
                                      {cell.fields?.map((field, index) => (
                                        <Box
                                          key={index}
                                          sx={(theme) => styles.configField(theme, { type: field.type })}
                                        >
                                          <Typography variant="subtitle2">
                                            {field.type === 'text'
                                              ? field.text
                                              : field.type === 'html'
                                                ? <div dangerouslySetInnerHTML={{ __html: field.html || '' }} />
                                                : `${field.table || '(table)'}.${field.field || '(field)'}`
                                            }
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  ) : (content.columns[columnIndex]?.type !== 'link' && (
                                    <input
                                      placeholder="Text"
                                      value={typeof cell === 'string' ? cell : cell.text}
                                      onChange={(e) =>
                                        handleChangeCell(rowIndex, columnIndex, e, typeof cell !== 'string')
                                      }
                                    />
                                  ))}
                                  {content.columns && content.columns[columnIndex]?.type === 'link' && (
                                    <>
                                      <input
                                        placeholder="Text"
                                        name="linkText"
                                        value={cell?.linkText}
                                        onChange={(e) => handleChangeLink(rowIndex, columnIndex, e)}
                                      />
                                      <input
                                        placeholder="URL"
                                        name="linkUrl"
                                        value={cell?.linkUrl}
                                        onChange={(e) => handleChangeLink(rowIndex, columnIndex, e)}
                                      />
                                    </>
                                  )}
                                  {content.columns && content.columns[columnIndex]?.type === 'avatar' && (
                                    <>
                                      {cell && <Avatar sx={{ mr: 10 }} src={cell} />}
                                      <Button size="small" onClick={() => handleOpenBrowser(rowIndex, columnIndex)}>
                                        {cell ? 'Change' : 'Upload'}
                                      </Button>
                                      <input
                                        style={{ display: 'none' }}
                                        type="file"
                                        ref={fileSelector}
                                        onChange={handleUpdateLogo}
                                      />
                                    </>
                                  )}
                                  <Box display="flex" sx={{ ml: 'auto' }}>
                                    {content.columns[columnIndex]?.showValueConfiguration && (
                                      <Tooltip title="Cell Config">
                                        <IconButton onClick={() => handleOpenCellConfigDialog(rowIndex, columnIndex)}>
                                          <Settings />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {columnIndex === 0 && (
                                      <Tooltip title="Row Confirm Settings">
                                        <IconButton sx={{ mr: 4 }} onClick={() => handleOpenRowConfirmDialog(rowIndex, columnIndex)}>
                                          <Settings />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {columnIndex === 0 && (
                                      <>
                                        <Tooltip title="Row Setting">
                                          <IconButton
                                            sx={{ svg: { flexShrink: 0, width: 22, height: 22 } }}
                                            onClick={() => handleOpenRowSetting(rowIndex)}
                                          >
                                            <RowSettingIcon />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove Row">
                                          <IconButton
                                            sx={{ svg: { flexShrink: 0, width: 22, height: 22 } }}
                                            onClick={() => handleRemoveRow(rowIndex)}
                                          >
                                            <RowDeleteIcon />
                                          </IconButton>
                                        </Tooltip>

                                      </>
                                    )}
                                  </Box>
                                </Box>
                              </MuiTableCell>
                            ))}
                          </MuiTableRow>
                        )}
                      </Draggable>
                    ))}
                  </MuiTableBody>
                </MuiTable>
              )}
            </Droppable>
          </DragDropContext>
        </Box>
      </Box>

      {!!columnSettingIndex && (
        <Dialog open={!!columnSettingIndex} onClose={handleCloseColumnSetting} sx={styles.dialog}>
          <Box sx={{ width: '100%' }}>
            <Box sx={{ padding: '0 20px' }}>
              <Box display="flex" alignItems="center">
                <Typography sx={{ mr: 10 }}>Type:</Typography>
                <Select
                  sx={{ flexGrow: 1 }}
                  value={content.columns && content.columns[columnSettingIndex - 1]?.type}
                  onChange={handleChangeColumnSetting}
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="avatar">Avatar</MenuItem>
                  <MenuItem value="link">Link</MenuItem>
                </Select>
              </Box>
              <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  label="Show value configuration"
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      disabled={content.type === 'Automatic'}
                      checked={content.columns && content.columns[columnSettingIndex - 1]?.showValueConfiguration}
                      color="secondary"
                      onChange={handleChangeValueConfigOption}
                    />
                  }
                />
              </Box>
              <Box sx={{ mt: 10, display: 'flex', flexDirection: 'column' }}>
                <FormControlLabel
                  label="Show Column Tooltip"
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      checked={content.columns && content.columns[columnSettingIndex - 1]?.showColumnTooltip}
                      color="secondary"
                      onChange={handleChangeAddToolTip}
                    />
                  }
                />
                {content.columns && content.columns[columnSettingIndex - 1]?.showColumnTooltip && (
                  <TextField
                    label="Tooltip Text"
                    color="secondary"
                    multiline
                    value={content.columns[columnSettingIndex - 1]?.columnTooltip}
                    onChange={handleChangeColumnTooltipText}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseColumnSetting}>Okay</Button>
            </Box>
          </Box>
        </Dialog>
      )}

      {selectedRowPos !== null && (
        <Dialog sx={styles.dialog} open={selectedRowPos !== null} onClose={handleCancelRowSetting}>
          <Box sx={{ px: 8, width: '100%', mt: -64 }}>
            <Typography variant="h6" sx={{ mb: 16 }}>
              Row Style Settings
            </Typography>
            {(() => {
              const rowSetting = stagedRowSetting || {};
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <TextField
                    label="Font Weight"
                    select
                    value={rowSetting.fontWeight || ''}
                    onChange={e => handleRowSettingFieldChange('fontWeight', e.target.value)}
                    color="secondary"
                    size="small"
                  >
                    <MenuItem value="">Default</MenuItem>
                    <MenuItem value="normal">Normal</MenuItem>
                    <MenuItem value="bold">Bold</MenuItem>
                    <MenuItem value="lighter">Lighter</MenuItem>
                    <MenuItem value="bolder">Bolder</MenuItem>
                  </TextField>
                  <Box sx={{ display: 'flex', gap: 16 }}>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Font Color</Typography>
                      <ColorPicker color={rowSetting.fontColor || '#000000'} onChange={(c) => handleRowSettingFieldChange('fontColor', c)}>
                        <Box sx={{ width: 120, height: 32, borderRadius: 1, border: '1px solid #ccc', backgroundColor: rowSetting.fontColor || '#000000' }} />
                      </ColorPicker>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Background Color</Typography>
                      <ColorPicker color={rowSetting.backgroundColor || '#ffffff'} onChange={(c) => handleRowSettingFieldChange('backgroundColor', c)}>
                        <Box sx={{ width: 120, height: 32, borderRadius: 1, border: '1px solid #ccc', backgroundColor: rowSetting.backgroundColor || '#ffffff' }} />
                      </ColorPicker>
                    </Box>
                  </Box>
                  {/* <TextField
                    label="Border Style"
                    select
                    value={rowSetting.borderStyle || ''}
                    onChange={e => handleRowSettingFieldChange('borderStyle', e.target.value)}
                    color="secondary"
                    size="small"
                  >
                    <MenuItem value="">Default</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="solid">Solid</MenuItem>
                    <MenuItem value="dashed">Dashed</MenuItem>
                    <MenuItem value="dotted">Dotted</MenuItem>
                    <MenuItem value="double">Double</MenuItem>
                    <MenuItem value="groove">Groove</MenuItem>
                    <MenuItem value="ridge">Ridge</MenuItem>
                    <MenuItem value="inset">Inset</MenuItem>
                    <MenuItem value="outset">Outset</MenuItem>
                  </TextField> */}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 8 }}>
                    <Button variant="outlined" onClick={handleCancelRowSetting}>Cancel</Button>
                    <Button variant="contained" color="secondary" onClick={handleCommitRowSetting}>Save</Button>
                  </Box>
                </Box>
              );
            })()}
          </Box>
        </Dialog>
      )}

      {selectedCellPos &&
        (content.type !== 'Automatic' ? (
          <CellConfigDialog
            isin={isin}
            config={content.type !== 'Automatic' ? content?.body[selectedCellPos[0]][selectedCellPos[1]] : undefined}
            open={!!selectedCellPos}
            onClose={handleCloseCellConfigDialog}
            onConfirm={handleSetCellConfig}
          />
        ) : (
          <AutomaticCellConfigDialog
            defaultConfig={content.body[0]?.find((item) => item.fields && item.fields.length > 0)?.fields[0].table}
            config={content?.body[selectedCellPos[0]][selectedCellPos[1]]}
            open={!!selectedCellPos}
            onClose={handleCloseCellConfigDialog}
            onConfirm={handleSetCellConfig}
          />
        ))}
      {!!confirmRowIndex && (
        <Dialog open={!!confirmRowIndex} onClose={handleCloseRowConfirmDialog} sx={styles.dialog}>
          <Box sx={{ px: 32, width: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Confirm Dialog Settings</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FormControlLabel
                  label="Enable Confirmation"
                  sx={{ width: 'fit-content' }}
                  control={
                    <Switch
                      checked={confirmEnabled}
                      color="secondary"
                      onChange={(e) => setConfirmEnabled(e.target.checked)}
                    />
                  }
                />
                <Button sx={{ ml: 'auto' }} onClick={handleApplyRowConfirmToColumn}>Apply to all rows</Button>
              </Box>
              {confirmEnabled && (
                <>
                  <TextField label="Title" color="secondary" value={confirmForm.title} onChange={handleChangeConfirmField('title')} />
                  <TextField label="Text" color="secondary" multiline value={confirmForm.text} onChange={handleChangeConfirmField('text')} />
                  <TextField label="Confirm Text" color="secondary" value={confirmForm.confirmText} onChange={handleChangeConfirmField('confirmText')} />
                  <TextField label="Cancel Text" color="secondary" value={confirmForm.cancelText} onChange={handleChangeConfirmField('cancelText')} sx={{ flexGrow: 1 }} />
                  <FormControlLabel
                    label="Show Cancel Alert"
                    sx={{ width: 'fit-content' }}
                    control={
                      <Switch
                        checked={showCancelAlert}
                        color="secondary"
                        onChange={(e) => setShowCancelAlert(e.target.checked)}
                      />
                    }
                  />
                  {showCancelAlert && (
                    <>
                      <TextField label="Cancel Alert Title" color="secondary" value={confirmForm.cancelAlertTitle} onChange={handleChangeConfirmField('cancelAlertTitle')} />
                      <TextField label="Cancel Alert Text" color="secondary" multiline value={confirmForm.cancelAlertText} onChange={handleChangeConfirmField('cancelAlertText')} />
                    </>
                  )}
                </>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 8, mt: 16 }}>
                <Button variant='outlined' onClick={handleCloseRowConfirmDialog}>Cancel</Button>
                <Button variant="contained" color="secondary" onClick={handleSaveRowConfirmDialog}>Save</Button>
              </Box>
            </Box>
          </Box>
        </Dialog>
      )}
    </Box>
  );
};
